import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Search,
  Users as UsersIcon,
  Pencil,
  Trash2,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import { StatsCard } from '../../components/ui/StatsCard';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { UserForm } from '../../components/forms/UserForm';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageAction } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { userApi, companyApi, roleApi } from '../../services/api-services';
import type { User, Company, ListQuery } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// User Management — Operations Deck (same shape as CompaniesPage)
// Form lives in components/forms/UserForm.tsx
// ─────────────────────────────────────────────────────────────

type RoleSegment = 'all' | 'admins' | 'users';

function isAdminRoleName(name?: string): boolean {
  if (!name) return false;
  return /admin/i.test(name);
}

/**
 * Resolve a user's role name. The list endpoint only includes the UserRoles
 * join (with roleId), not the Role entity itself — so we look the name up
 * from the loaded roles list. Falls back to a nested role.roleName if the
 * backend ever starts returning it, then to "—".
 */
function userRoleName(u: User, roleMap: Map<number, string>): string {
  const first = u.userRoles?.[0];
  if (!first) return '—';
  return (
    first.role?.roleName
    ?? (first.roleId != null ? roleMap.get(first.roleId) : undefined)
    ?? '—'
  );
}

function roleBadgeStyle(roleName: string): { bg: string; fg: string; border: string } {
  if (/superadmin/i.test(roleName)) {
    return {
      bg: 'rgba(185,28,28,0.10)',
      fg: 'var(--color-bad)',
      border: 'rgba(185,28,28,0.25)',
    };
  }
  if (/admin/i.test(roleName)) {
    return {
      bg: 'rgba(183,121,31,0.10)',
      fg: 'var(--color-warn)',
      border: 'rgba(183,121,31,0.30)',
    };
  }
  return {
    bg: 'rgba(21,128,141,0.10)',
    fg: 'var(--color-teal)',
    border: 'rgba(21,128,141,0.28)',
  };
}

export function UsersPage() {
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const isCompanyAdmin = authUser?.role === 'CompanyAdmin';
  const authUsername = authUser?.username;
  const authCompanyId = authUser?.companyId != null ? Number(authUser.companyId) : null;

  // Shared CSS Grid column template — wider for SuperAdmin to include Company column.
  const COL_TEMPLATE = isSuperAdmin
    ? 'minmax(220px,1.5fr) minmax(220px,1.4fr) minmax(140px,0.9fr) minmax(170px,1.2fr) minmax(120px,0.9fr) 88px'
    : 'minmax(220px,1.5fr) minmax(220px,1.4fr) minmax(140px,0.9fr) minmax(120px,0.9fr) 88px';

  // Filters
  const [roleSegment, setRoleSegment] = useState<RoleSegment>('all');
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [roleNameFilter, setRoleNameFilter] = useState('');

  // Server-side fetch — SuperAdmin sees all; everyone else sees own company.
  const fetchFn = useCallback(
    (q: ListQuery) =>
      isSuperAdmin
        ? userApi.getAll(q)
        : userApi.getAllByCompanyId(authCompanyId ?? 0, q),
    [isSuperAdmin, authCompanyId],
  );

  const crud = useCrudPage<User>({ fetchFn, initialSortCol: 'Username' });
  const [saving, setSaving] = useState(false);

  // Reference data
  const [companies, setCompanies] = useState<Company[]>([]);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);

  const recordActivity = useActivityStore((s) => s.record);

  // roleId → role name map. Rebuilt whenever the roles list changes.
  const roleMap = useMemo(() => {
    const m = new Map<number, string>();
    roles.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [roles]);

  useEffect(() => {
    if (isSuperAdmin) {
      companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? []));
      roleApi.getAll().then((r) => setRoles(r.entities ?? [])).catch(() => {});
    } else if (isCompanyAdmin) {
      roleApi.getAdminRoles().then((r) => setRoles(r.entities ?? [])).catch(() => {});
      // Still load companies so we can resolve our own company name for the readonly field.
      companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? []));
    } else {
      roleApi.getUserRoles().then((r) => setRoles(r.entities ?? [])).catch(() => {});
      companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? []));
    }
  }, [isSuperAdmin, isCompanyAdmin]);

  // Client-side filter pass on the current page.
  const filteredData = useMemo(() => {
    return crud.data.filter((u) => {
      const r = userRoleName(u, roleMap);
      if (roleSegment === 'admins' && !isAdminRoleName(r)) return false;
      if (roleSegment === 'users' && isAdminRoleName(r)) return false;
      if (roleNameFilter && r !== roleNameFilter) return false;
      if (companyFilter !== '' && u.companyId !== companyFilter) return false;
      return true;
    });
  }, [crud.data, roleMap, roleSegment, roleNameFilter, companyFilter]);

  const availableRoles = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((u) => {
      const r = userRoleName(u, roleMap);
      if (r !== '—') s.add(r);
    });
    // Also include the full roles list so the filter is useful even on an empty page.
    roles.forEach((r) => s.add(r.name));
    return Array.from(s).sort();
  }, [crud.data, roleMap, roles]);

  const availableCompanies = useMemo(() => {
    const map = new Map<number, string>();
    crud.data.forEach((u) => {
      if (u.companyId != null && u.company?.companyName) {
        map.set(u.companyId, u.company.companyName);
      }
    });
    // Augment with full company list for SuperAdmin's drop-down.
    if (isSuperAdmin) companies.forEach((c) => map.set(c.id, c.companyName));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [crud.data, companies, isSuperAdmin]);

  const filtersActive = roleNameFilter !== '' || companyFilter !== '' || roleSegment !== 'all';
  const clearFilters = () => {
    setRoleNameFilter('');
    setCompanyFilter('');
    setRoleSegment('all');
  };

  // Stats
  const stats = useMemo(() => {
    const onPage = crud.data;
    const admins = onPage.filter((u) => isAdminRoleName(userRoleName(u, roleMap))).length;
    const standard = onPage.filter((u) => !isAdminRoleName(userRoleName(u, roleMap))).length;
    const recentCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = onPage.filter((u) => {
      if (!u.createdDate) return false;
      const t = new Date(u.createdDate).getTime();
      return Number.isFinite(t) && t >= recentCutoff;
    }).length;
    return { total: crud.totalCount, admins, standard, recent };
  }, [crud.data, crud.totalCount, roleMap]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // ── CRUD wiring ────────────────────────────────────────────
  const handleSave = async ({
    form,
    roleId,
    confirmPassword: _confirmPassword,
  }: {
    form: Partial<User>;
    roleId: number;
    confirmPassword: string;
  }) => {
    setSaving(true);
    try {
      const selectedRole = roles.find((r) => r.id === roleId);
      const selectedCompany = companies.find((c) => c.id === form.companyId);
      const fullName = [form.firstName, form.lastName].filter(Boolean).join(' ').trim();
      const label = fullName || form.email || form.username || 'Untitled user';

      const payload = {
        id: form.id ?? 0,
        username: form.username ?? '',
        email: form.email ?? '',
        password: form.password ?? '',
        companyId: form.companyId ?? null,
        firstName: form.firstName ?? '',
        lastName: form.lastName ?? '',
        // Preserve V2 hidden fields if present on the edited record
        initials: String((form as unknown as Record<string, unknown>).initials ?? ''),
        address: String((form as unknown as Record<string, unknown>).address ?? ''),
        city: String((form as unknown as Record<string, unknown>).city ?? ''),
        stateProvince: String((form as unknown as Record<string, unknown>).stateProvince ?? ''),
        postalZipCode: String((form as unknown as Record<string, unknown>).postalZipCode ?? ''),
        country: String((form as unknown as Record<string, unknown>).country ?? ''),
        roleId,
        companyName: selectedCompany?.companyName ?? '',
        roleName: selectedRole?.name ?? '',
      };
      const res = crud.editingItem ? await userApi.update(payload) : await userApi.create(payload);
      if (res?.status === 'Failed') {
        crud.showToast(res.error?.errorMessage || 'Failed to save user', 'error');
        return;
      }
      crud.showToast(crud.editingItem ? 'User updated successfully' : 'User created successfully');
      recordActivity({
        type: crud.editingItem ? 'update' : 'create',
        entity: 'Users',
        label,
        user: authUsername,
      });
      crud.closeModal();
      crud.load();
    } catch {
      crud.showToast('Failed to save user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, fullName?: string) => {
    if (id === Number(authUser?.userId)) {
      crud.showToast("You can't delete your own account.", 'error');
      return;
    }
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await userApi.delete(id);
      crud.showToast('User deleted');
      recordActivity({
        type: 'delete',
        entity: 'Users',
        label: fullName?.trim() || `User #${id}`,
        user: authUsername,
      });
      crud.load();
    } catch {
      crud.showToast('Failed to delete user', 'error');
    }
  };

  // Publish the page's primary action into the TopBar slot.
  usePageAction(
    <PrimaryButton onClick={crud.openCreate}>
      <Plus size={14} /> Add user
    </PrimaryButton>,
    [],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Stats strip */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatsCard
          label="Registered"
          value={stats.total}
          sub="Users in portal"
          accent="var(--color-accent)"
          loading={crud.loading}
        />
        <StatsCard
          label="Admins (this page)"
          value={stats.admins}
          sub="SuperAdmin + CompanyAdmin"
          accent="var(--color-warn)"
          loading={crud.loading}
        />
        <StatsCard
          label="Standard (this page)"
          value={stats.standard}
          sub="Company users"
          accent="var(--color-teal)"
          loading={crud.loading}
        />
        <StatsCard
          label="New (this page)"
          value={stats.recent}
          sub="Created in last 30 days"
          accent="#7c6ba8"
          loading={crud.loading}
        />
      </div>

      {/* Filter / search bar */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 280,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search users…"
              value={crud.searchText}
              onChange={(e) => {
                crud.setSearchText(e.target.value);
                crud.setOffset(0);
              }}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-dim)';
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Role segmented */}
          <RoleSegmented value={roleSegment} onChange={setRoleSegment} />

          {/* Filter popover (Role + Company) */}
          <FilterPopover
            companyFilter={companyFilter}
            roleNameFilter={roleNameFilter}
            availableCompanies={availableCompanies}
            availableRoles={availableRoles}
            isSuperAdmin={!!isSuperAdmin}
            onChangeCompany={setCompanyFilter}
            onChangeRole={setRoleNameFilter}
            onClear={clearFilters}
            activeCount={(companyFilter !== '' ? 1 : 0) + (roleNameFilter ? 1 : 0)}
          />

          {filtersActive && (
            <button
              onClick={clearFilters}
              style={{
                fontSize: 12,
                color: 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 8px',
                borderRadius: 5,
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(11,74,111,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : filtersActive
                ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
                : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COL_TEMPLATE,
            padding: '12px 18px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            fontSize: 12.5,
            letterSpacing: 0.3,
            color: 'var(--color-text-mid)',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
          }}
        >
          <SortHeader label="User" col="Username" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Email" col="Email" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <div>Role</div>
          {isSuperAdmin && (
            <SortHeader label="Company" col="CompanyId" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          )}
          <SortHeader label="Created" col="CreatedDate" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {/* Rows */}
        {crud.loading ? (
          <div
            style={{
              padding: 60,
              textAlign: 'center',
              color: 'var(--color-text-dim)',
              fontSize: 13,
            }}
          >
            Loading…
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <UsersIcon size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              {filtersActive ? 'No users match these filters' : 'No users found'}
            </div>
            <div style={{ fontSize: 12 }}>
              {filtersActive
                ? 'Try clearing filters or moving to another page.'
                : 'Add a new user to get started.'}
            </div>
          </div>
        ) : (
          <div>
            {filteredData.map((u, i) => (
              <UserRow
                key={u.id ?? i}
                user={u}
                roleMap={roleMap}
                isSuperAdmin={!!isSuperAdmin}
                colTemplate={COL_TEMPLATE}
                isLast={i === filteredData.length - 1}
                isSelf={u.id === Number(authUser?.userId)}
                onEdit={() => crud.openEdit(u)}
                onDelete={() =>
                  u.id && handleDelete(u.id, [u.firstName, u.lastName].filter(Boolean).join(' '))
                }
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>
              {filteredData.length}
            </span>
            {filtersActive && filteredData.length !== crud.data.length && (
              <> (of {crud.data.length} on page)</>
            )}{' '}
            of{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span>{' '}
            users
          </div>
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: 'var(--font-mono)',
              }}
            >
              <PaginationButton
                onClick={() => crud.setOffset(Math.max(0, crud.offset - crud.pageSize))}
                disabled={crud.offset === 0}
              >
                ←
              </PaginationButton>
              <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
                {currentPage} / {totalPages}
              </span>
              <PaginationButton
                onClick={() => crud.setOffset(crud.offset + crud.pageSize)}
                disabled={currentPage >= totalPages}
              >
                →
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <UserForm
        open={crud.modalOpen}
        editing={crud.editingItem}
        saving={saving}
        isSuperAdmin={!!isSuperAdmin}
        authCompanyId={authCompanyId}
        companies={companies}
        roles={roles}
        onCancel={crud.closeModal}
        onSave={handleSave}
      />

      {crud.toast && (
        <Toast
          message={crud.toast.message}
          type={crud.toast.type}
          onClose={() => crud.setToast(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Row + cells
// ─────────────────────────────────────────────────────────────

function UserRow({
  user: u,
  roleMap,
  isSuperAdmin,
  colTemplate,
  isLast,
  isSelf,
  onEdit,
  onDelete,
}: {
  user: User;
  roleMap: Map<number, string>;
  isSuperAdmin: boolean;
  colTemplate: string;
  isLast: boolean;
  isSelf: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || '—';
  const roleName = userRoleName(u, roleMap);
  const roleStyle = roleBadgeStyle(roleName);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        padding: '14px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        fontSize: 14,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--color-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {fullName}
          {isSelf && (
            <span
              style={{
                fontSize: 10,
                padding: '1px 6px',
                borderRadius: 4,
                background: 'var(--color-panel-alt)',
                color: 'var(--color-text-mid)',
                fontFamily: 'var(--font-mono)',
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                border: '1px solid var(--color-border)',
              }}
            >
              You
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--color-text-dim)',
            marginTop: 3,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {u.username || '—'}
        </div>
      </div>

      <Cell
        mono
        ellipsis
        style={{
          color: u.email ? 'var(--color-accent)' : 'var(--color-text-dim)',
        }}
      >
        {u.email || '—'}
      </Cell>

      <div>
        <span
          style={{
            display: 'inline-block',
            padding: '3px 8px',
            borderRadius: 5,
            background: roleStyle.bg,
            color: roleStyle.fg,
            border: `1px solid ${roleStyle.border}`,
            fontSize: 11.5,
            fontWeight: 500,
            letterSpacing: 0.2,
          }}
        >
          {roleName}
        </span>
      </div>

      {isSuperAdmin && (
        <Cell dim ellipsis>
          {u.company?.companyName ?? '—'}
        </Cell>
      )}

      <Cell mono dim>
        {formatDate(u.createdDate)}
      </Cell>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <IconButton title="Edit" onClick={onEdit}>
          <Pencil size={15} />
        </IconButton>
        <IconButton
          title={isSelf ? 'You cannot delete yourself' : 'Delete'}
          onClick={onDelete}
          hoverDanger
          disabled={isSelf}
        >
          <Trash2 size={15} />
        </IconButton>
      </div>
    </div>
  );
}

function Cell({
  children,
  mono = false,
  dim = false,
  ellipsis = false,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  dim?: boolean;
  ellipsis?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        color: dim ? 'var(--color-text-mid)' : 'var(--color-text)',
        fontSize: mono ? 13 : 14,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const idleBg = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';
  const active = hover && !disabled;

  return (
    <button
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        border: `1px solid ${active ? hoverBorder : idleBorder}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? hoverBg : idleBg,
        color: active ? hoverColor : idleColor,
        opacity: disabled ? 0.4 : 1,
        transition: 'all 120ms',
        transform: active ? 'translateY(-1px)' : 'none',
        boxShadow: active ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        background: hover ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        color: '#fff',
        border: 'none',
        padding: '9px 14px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Role segmented control + Filter popover
// ─────────────────────────────────────────────────────────────

function RoleSegmented({
  value,
  onChange,
}: {
  value: RoleSegment;
  onChange: (v: RoleSegment) => void;
}) {
  const opts: { v: RoleSegment; label: string; dot?: string }[] = [
    { v: 'all', label: 'All' },
    { v: 'admins', label: 'Admins', dot: 'var(--color-warn)' },
    { v: 'users', label: 'Users', dot: 'var(--color-teal)' },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        gap: 2,
      }}
    >
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              border: 'none',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              background: active ? 'var(--color-panel)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-mid)',
              boxShadow: active ? '0 1px 2px rgba(15,23,32,0.06)' : 'none',
              transition: 'all 120ms',
            }}
          >
            {o.dot && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: o.dot,
                  display: 'inline-block',
                }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterPopover({
  companyFilter,
  roleNameFilter,
  availableCompanies,
  availableRoles,
  isSuperAdmin,
  onChangeCompany,
  onChangeRole,
  onClear,
  activeCount,
}: {
  companyFilter: number | '';
  roleNameFilter: string;
  availableCompanies: { id: number; name: string }[];
  availableRoles: string[];
  isSuperAdmin: boolean;
  onChangeCompany: (v: number | '') => void;
  onChangeRole: (v: string) => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const POP_WIDTH = 340;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 8;
    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - POP_WIDTH - 12;
    const left = Math.max(12, Math.min(desiredLeft, maxLeft));
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 7,
          transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: 'var(--color-accent)',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: POP_WIDTH,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Filter by
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-dim)',
                borderRadius: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PopoverField label="Role">
              <FilterSelect
                value={roleNameFilter}
                onChange={onChangeRole}
                placeholder="Any role"
                options={availableRoles}
              />
            </PopoverField>
            {isSuperAdmin && (
              <PopoverField label="Company">
                <FilterSelect
                  value={companyFilter === '' ? '' : String(companyFilter)}
                  onChange={(v) => onChangeCompany(v === '' ? '' : Number(v))}
                  placeholder="Any company"
                  options={availableCompanies.map((c) => ({ value: String(c.id), label: c.name }))}
                />
              </PopoverField>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
            }}
          >
            <button
              onClick={onClear}
              disabled={activeCount === 0}
              style={{
                fontSize: 12,
                color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: activeCount === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                padding: '4px 4px',
              }}
            >
              Clear filters
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                background: 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--color-text-mid)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

// `options` can be string[] or { value, label }[].
type FilterOption = string | { value: string; label: string };
function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: FilterOption[];
  placeholder: string;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        height: 34,
        padding: '0 30px 0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
        background: `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        outline: 'none',
        cursor: 'pointer',
        fontWeight: hasValue ? 500 : 400,
        transition: 'border-color 120ms',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const value = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        return (
          <option key={value} value={value}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

// ── helpers ──────────────────────────────────────────────────
function formatDate(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return d;
  }
}
